/* eslint-disable node/prefer-global/process */
import type { AggregatePageProp } from '@autobid/ui/utils/car/getAggregateBidSlug'
import auctionSlugs from '@autobid/apps-nuxt-config/i18n/auctionSlugs'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { getLanguageHeader } from '@autobid/strapi-integration/utils/getLanguageHeader'
import { validateUrl } from '@autobid/ui/composables/useOpenLink'
import { AGGREGATE_BID_ID } from '@autobid/ui/constants/AGGREGATE_BID_ID'
import { getAggregateBidSlug } from '@autobid/ui/utils/car/getAggregateBidSlug'
import { setResponseHeader } from 'h3'

interface Props {
  id?: string
  show: string
  action: string
  lang: string
  appId: number
}

async function useCarRedirect({ show, appId, id, lang }: Props) {
  const ALLOWED_SHOWS = ['car', 'details']
  const { $trpc } = useNuxtApp()
  const pathKey
    = show === 'details' ? 'item/[slug]/details' : 'item/[slug]/index'
  const EMPTY_RETURN_STATE = {
    slug: 'not-found',
    appId,
    pathKey,
  }

  if (!ALLOWED_SHOWS.includes(show))
    return

  if (Number.isNaN(Number(id)) || (typeof id === 'string' && !id.length)) {
    return EMPTY_RETURN_STATE
  }

  try {
    const result = await $trpc.auctions.getRedirectCarData.query({ carId: Number(id), lang })

    const metadata = result?.data?.singleItem?.metadata

    if (metadata?.error_code && metadata.error_code !== 404) {
      throw createError({
        statusCode: metadata.error_code,
        statusMessage: metadata.message,
      })
    }

    const { slug, appId: carAppId }
      = result?.data?.singleItem?.item?.itemData ?? EMPTY_RETURN_STATE

    return {
      slug,
      appId: carAppId,
      pathKey,
    }
  }
  catch (error) {
    const { message, code } = getError(error)

    throw createError({ statusCode: code, statusMessage: message })
  }
}

async function useCarsListRedirect({ lang, appId, id }: Props) {
  const { $trpc } = useNuxtApp()

  if (Number.isNaN(Number(id)))
    return

  try {
    const result = await $trpc.auctions.getRedirectAuctionData.query({ auctionId: Number(id), lang })

    const metadata = result.data?.singleAuction.metadata

    if (metadata?.error_code && metadata.error_code !== 404) {
      throw createError({
        statusCode: metadata.error_code,
        statusMessage: metadata.message,
      })
    }

    const REJECTED_RESPONSE = {
      slug: id,
      pathKey: 'auction/[slug]',
    }

    if (result.data?.singleAuction.item?.appId !== appId)
      return REJECTED_RESPONSE

    const { slug: slugs } = result.data.singleAuction.item
    const slug = slugs?.[lang.toUpperCase()]

    return {
      slug: slug ?? id,
      pathKey: 'auction/[slug]',
    }
  }
  catch (error) {
    const { message, code } = getError(error)

    throw createError({ statusCode: code, statusMessage: message })
  }
}

async function useCurrentCarRedirect(props: Props) {
  const result = await useCarsListRedirect(props)

  if (!result)
    return result

  result.pathKey = 'auction/current/[slug]'

  return result
}

function useGetApp() {
  const { $trpc } = useNuxtApp()

  const getAppUrl = async (appId: number, lang?: string) => {
    try {
      const apps = await $trpc.auctions.getApplications.query({ lang })

      return apps?.items?.find(app => app.id === appId)?.url
    }
    catch (error) {
      const { message, code } = getError(error)

      throw createError({ statusCode: code, statusMessage: message })
    }
  }

  return { getAppUrl }
}

function useGetUserLanguage() {
  if (process.client)
    return

  const event = useRequestEvent()

  if (!event) {
    return
  }

  return getLanguageHeader(event)
}

function findSupportedLocale(locales: string[]) {
  return locales
    .find(locale => auctionSlugs['auction/[slug]'][locale])
    ?.trim()
}

/**
 * Middleware that redirects from old car pages (from the old version of premium) e.g.
  - ?action=cars&auction=64614 - cars list
  - ?action=car&id=581998&show=car - barometer
  - /item/[id] - barometer - additionally redirect to the other apps as well
  - ?action=car&id=581998&show=details - details
  - /details/[id] - details - additionally redirect to the other apps as well
  - ?action=car&show=next&auction=64614 - current barometer
 */

export default defineNuxtRouteMiddleware(async (to) => {
  const { auction } = to.query
  const { getAppUrl } = useGetApp()
  const { APP_ID, autobidCMS } = useRuntimeConfig().public

  const userLanguage = useGetUserLanguage()
  const splittedPath = to.path.split('/').filter(el => el.length)
  const isLocaleSupported = auctionSlugs['auction/[slug]'][splittedPath[0]]
  const lang = findSupportedLocale([
    splittedPath[0],
    userLanguage || '',
    autobidCMS?.language,
    'en',
  ])
  const rest = splittedPath.slice(
    isLocaleSupported ? 1 : 0,
    splittedPath.length,
  )

  const SUPPORTED_ACTIONS = ['car', 'cars']

  const isDetailsOrItemPage
    = (rest.length === 2 && !Number.isNaN(+rest[1]) && rest[0] === 'item')
      || rest[0] === 'details'
  const action = isDetailsOrItemPage ? 'car' : String(to.query.action)
  const show = (() => {
    if (!isDetailsOrItemPage)
      return to.query.show

    return rest[0] === 'item' ? 'car' : 'details'
  })()
  const id = isDetailsOrItemPage ? rest[1] : to.query.id

  if (!action || !SUPPORTED_ACTIONS.includes(action))
    return

  const callbacks = {
    cars: useCarsListRedirect,
    car: useCarRedirect,
    current: useCurrentCarRedirect,
  }
  const targetId = String(id ?? auction ?? rest[2])
  const callbackName = (action === 'car' && show === 'next' ? 'current' : action) as keyof typeof callbacks

  const INVALID_DATA = { slug: null, pathKey: null }
  const callbackData = callbacks[callbackName]
    ? (await callbacks[callbackName]({
        show: String(show),
        action,
        lang: String(lang),
        appId: Number(APP_ID),
        id: targetId,
      })) ?? INVALID_DATA
    : INVALID_DATA

  const slug = callbackData.slug
  const pathKey = callbackData.pathKey
  const appId = 'appId' in callbackData && callbackData.appId ? Number(callbackData.appId) : undefined

  if (!slug || !pathKey) {
    return navigateTo({ path: `/${lang}` }, { replace: true })
  }

  const path
    = appId === AGGREGATE_BID_ID
      ? `/${getAggregateBidSlug(
        Number(targetId),
        String(lang),
        show as AggregatePageProp,
      )}`
      : `/${lang}${auctionSlugs[pathKey as keyof typeof auctionSlugs][String(lang)].replace('[slug]', slug)}`

  const isTemporaryNotFoundRedirect = slug === 'not-found'
  const redirectCode = isTemporaryNotFoundRedirect ? 302 : 301

  if (isTemporaryNotFoundRedirect && process.server) {
    const event = useRequestEvent()

    if (event) {
      setResponseHeader(event, 'Cache-Control', 'no-store')
    }
  }

  if (appId && appId !== Number(APP_ID)) {
    const appUrl = await getAppUrl(appId, lang)

    if (appUrl) {
      const appUrlWithoutTrailingSlash = appUrl.replace(/\/$/, '')
      const safeLink = validateUrl(`${appUrlWithoutTrailingSlash}${path}`, appUrlWithoutTrailingSlash)

      if (!safeLink) {
        return
      }

      return navigateTo(safeLink, {
        redirectCode,
        external: true,
      })
    }
  }

  return navigateTo({ path }, { redirectCode, replace: true })
})
